import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { io } from "socket.io-client";
import { Button } from "@mui/material";
import GetCookieByName from "../../hooks/GetCookie";

// Renders no UI — mounted once as a sibling of <Routes> in App.js, inside BrowserRouter,
// so it survives every in-app navigation (React Router only swaps the matched route's own
// element; a sibling of <Routes> is untouched). That persistence is the whole point: this
// is the one socket connection in the app meant to outlive whichever page you're on, so a
// badge/toast can fire regardless of where you currently are. Every other socket usage in
// this app (WhatsAppInboxPage.jsx, CreatorPaymentHistory.jsx) is deliberately per-page and
// reconnects on navigation — copying that pattern here would silently drop events during
// every reconnect gap, the same bug already found and fixed once in the WhatsApp Inbox page
// itself.
//
// Originally built (and named) for WhatsApp reply notifications alone; now also carries
// Brief-assignment notifications, which is why it lives here in components/common rather
// than components/whatsapp — one persistent socket, multiple unrelated event types, each
// with its own cache key / badge / toast. Add further event types the same way rather than
// opening a second connection.
//
// No 'join' room emits needed for either feature — the backend already auto-joins every
// connected socket to its own personal room keyed by user id (helpers/socket.js), and both
// 'whatsapp-reply-notify' and 'brief-assigned-notify' are emitted directly to that room
// (routes/whatsappWebhook.js, routes/briefs.js), never a shared broadcast one. Just being
// connected is enough to receive either.
function NotificationsListener() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  // The socket effect below runs once (mount-only) so its closure can't see a fresh
  // `location` on every render — this ref is the standard escape hatch: updated by a
  // separate, cheap effect that does NOT touch the socket, read from inside the
  // long-lived event handlers instead of closing over the stale value directly.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    let authToken;
    try {
      authToken = JSON.parse(GetCookieByName("auth"))["token"];
    } catch (e) {
      return undefined; // not logged in (or on a public route) — nothing to connect for
    }
    if (!authToken) return undefined;

    const socket = io(new URL(process.env.REACT_APP_API_URL).origin, { auth: { token: authToken } });

    socket.on("connect_error", (err) => console.error("[notify] socket connect_error:", err.message));

    socket.on("whatsapp-reply-notify", ({ conversationId, customerName, text }) => {
      queryClient.setQueryData(["whatsapp-unread"], (n) => (n || 0) + 1);

      // Already on the Inbox looking at live updates directly — a toast on top would just
      // be noise repeating what's already visible there.
      if (pathnameRef.current.startsWith("/whatsapp/inbox")) return;

      enqueueSnackbar(`${customerName || "New message"}: ${text}`, {
        variant: "info",
        // Longer than the app-wide 2000ms default (App.js's SnackbarProvider) — this one's
        // actionable, not a fire-and-forget confirmation.
        autoHideDuration: 8000,
        action: (key) => (
          <Button
            size="small"
            sx={{ color: "#fff", textTransform: "none" }}
            onClick={() => {
              navigate("/whatsapp/inbox", { state: { conversationId } });
              closeSnackbar(key);
            }}
          >
            View
          </Button>
        ),
      });
    });

    socket.on("email-reply-notify", ({ conversationId, influencerEmail, subject }) => {
      queryClient.setQueryData(["email-unread"], (n) => (n || 0) + 1);

      // Already on the Email Inbox looking at live updates directly — same reasoning as
      // the WhatsApp case above.
      if (pathnameRef.current.startsWith("/email/inbox")) return;

      // Subject only, not the body — an email reply's body is often long-form prose that
      // swamps a toast, unlike WhatsApp's short chat-style `text` above.
      enqueueSnackbar(`${influencerEmail || "New reply"}: ${subject || "New reply"}`, {
        variant: "info",
        autoHideDuration: 8000,
        action: (key) => (
          <Button
            size="small"
            sx={{ color: "#fff", textTransform: "none" }}
            onClick={() => {
              navigate("/email/inbox", { state: { conversationId } });
              closeSnackbar(key);
            }}
          >
            View
          </Button>
        ),
      });
    });

    socket.on("brief-assigned-notify", ({ briefId, briefTitle, actorName }) => {
      queryClient.setQueryData(["brief-unread"], (n) => (n || 0) + 1);

      // Already looking at Briefs — same reasoning as the WhatsApp case above.
      if (pathnameRef.current.startsWith("/brief")) return;

      enqueueSnackbar(`${actorName || "Someone"} assigned you a brief: ${briefTitle}`, {
        variant: "info",
        autoHideDuration: 8000,
        action: (key) => (
          <Button
            size="small"
            sx={{ color: "#fff", textTransform: "none" }}
            onClick={() => {
              // Briefs already support direct-linking via /brief/:briefId (same URL the
              // email notification's own briefLink() builds), so a real navigate is
              // enough — no router-state trick needed like the WhatsApp case above.
              navigate(`/brief/${briefId}`);
              closeSnackbar(key);
            }}
          >
            View
          </Button>
        ),
      });
    });

    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export default NotificationsListener;
